import type * as Phaser from 'phaser';

/**
 * Returns a font-size in px that is enlarged on touch devices so menu
 * buttons and how-to-play text stay tappable / legible on small screens.
 * Mouse / pointer-only devices get the base size unchanged.
 */
export function responsiveFontSize(scene: Phaser.Scene, base: number): number {
  const touch = scene.sys.game.device.input.touch;
  return touch ? Math.max(base, Math.round(base * 1.4)) : base;
}

/**
 * Structural type for any Phaser GameObject that supports `getBounds()` and
 * `setInteractive()`. Covers Text, Shape subclasses (Rectangle, Triangle, …),
 * Image, Sprite, etc.
 */
interface InteractiveWithBounds {
  getBounds(): { width: number; height: number };
  setInteractive(
    hitArea: InstanceType<typeof Phaser.Geom.Rectangle>,
    callback: (
      hitArea: InstanceType<typeof Phaser.Geom.Rectangle>,
      x: number,
      y: number,
      gameObject: unknown,
    ) => boolean,
  ): unknown;
}

/**
 * Expands the interactive hit area of a game object to at least 44×44 px on
 * touch devices. On pointer/mouse devices this is a no-op.
 *
 * Accepts the Phaser.Geom.Rectangle class from the caller so this module does
 * not import Phaser as a value (which would break the lightweight unit-test
 * environment that has no canvas).
 *
 * Works with any game object that has `getBounds()` and `setInteractive()` —
 * Text, Rectangle, Triangle, Image, Sprite, etc.
 *
 * Optional `overrideW` / `overrideH` parameters allow callers to supply the
 * logical size when `getBounds()` would return an inaccurate value (e.g. a
 * tiny triangle whose rendered bounding box is smaller than the intended tap
 * target). When supplied the override values are used in place of `getBounds`.
 */
export function ensureTouchHitArea(
  obj: InteractiveWithBounds,
  scene: Phaser.Scene,
  RectClass: typeof Phaser.Geom.Rectangle,
  overrideW?: number,
  overrideH?: number,
): void {
  if (!scene.sys.game.device.input.touch) return;
  const bounds = obj.getBounds();
  const minSide = 44;
  const w = Math.max(overrideW ?? bounds.width, minSide);
  const h = Math.max(overrideH ?? bounds.height, minSide);
  const naturalW = overrideW ?? bounds.width;
  const naturalH = overrideH ?? bounds.height;
  obj.setInteractive(
    new RectClass(-((w - naturalW) / 2), -((h - naturalH) / 2), w, h),
    (hitArea, x, y) => RectClass.Contains(hitArea, x, y),
  );
}
